import React from 'react';
import { forwardRef } from 'react';
import {
    Box, Typography, Card, CardContent, Paper, Table, TableBody, TableCell, TableRow, TableHead,
} from '@mui/material';

const PrintPage = forwardRef(({ fetchedData, formData, propertyName, shiftName }, ref) => {
    return (
        <Box
            ref={ref}
            sx={{
                p: { xs: 1.5, sm: 2 },
                fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
                backgroundColor: '#fff',
                color: '#1a1a1a',
                minHeight: '100vh',
                '@media print': {
                    size: 'A4 portrait',
                    margin: 0,
                    backgroundColor: '#fff',
                },
            }}
        >
            {/* Tiny Header */}
            <Paper
                elevation={0}
                sx={{
                    p: 1.5,
                    mb: 2,
                    textAlign: 'center',
                    borderRadius: 1.5,
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                }}
            >
                <Typography
                    variant="h5"
                    sx={{
                        fontWeight: 700,
                        color: '#2c3e50',
                        fontSize: { xs: '1.3rem', sm: '1.5rem' },
                        lineHeight: 1.2,
                    }}
                >
                    Manpower Deployment
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        color: '#555',
                        fontSize: '1rem',
                        lineHeight: 1.3,
                        display: 'block',
                    }}
                >
                    {propertyName} • {shiftName} • {formData.DateField}
                    {formData.remark && ` • ${formData.remark}`}
                </Typography>
            </Paper>

            {/* Ultra-Compact Cards */}
            {fetchedData.map((station) => (
                <Card
                    key={station.HkWrkStnId}
                    sx={{
                        mb: 1.8,
                        borderRadius: 1.5,
                        overflow: 'hidden',
                        boxShadow: '0 2px 6px rgba(0,0,0,0.07)',
                        border: '1px solid #e3e6f0',
                        pageBreakInside: 'avoid',
                    }}
                >
                    <Box
                        sx={{
                            background: 'linear-gradient(135deg, #5c6bc0 0%, #512da8 100%)',
                            color: '#fff',
                            p: 0.8,
                            textAlign: 'center',
                        }}
                    >
                        <Typography
                            variant="subtitle2"
                            sx={{
                                fontWeight: 600,
                                fontSize: '0.8rem',
                                letterSpacing: '0.5px',
                                textTransform: 'uppercase',
                                lineHeight: 1.2,
                            }}
                        >
                            Work Station → {station.HkWrkStnName}
                        </Typography>
                    </Box>

                    <CardContent sx={{ p: 0 }}>
                        <Table size="small" sx={{ m: 0 }}>
                            <TableHead>
                                <TableRow
                                    sx={{
                                        backgroundColor: '#f1f3f5',
                                        height: 28,
                                        '& th': {
                                            fontWeight: 700,
                                            fontSize: '0.68rem',
                                            color: '#495057',
                                            p: '4px 6px',
                                            lineHeight: 1.2,
                                        },
                                    }}
                                >
                                    <TableCell sx={{ textAlign: 'left' }}>Worker Type</TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>Total Worker</TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>Task Status</TableCell>
                                    <TableCell sx={{ textAlign: 'center' }}>Sign</TableCell>
                                    <TableCell sx={{ textAlign: 'left', width: '300px' }}>Remark</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                {station.workerTypes
                                    .filter(worker => worker.DplyWrkr > 0)
                                    .map((worker, idx) => (
                                        <TableRow
                                            key={worker.HkWrkrTypeId}
                                            sx={{
                                                height: 26,
                                                backgroundColor: idx % 2 === 0 ? '#fdfdfd' : '#fafafa',
                                                '&:last-child td': { borderBottom: 'none' }
                                            }}
                                        >
                                            <TableCell sx={{ p: '3px 6px', fontSize: '0.72rem', fontWeight: 500, color: '#2c3e50', lineHeight: 1.2 }}>
                                                {worker.HkWrkTypeName}
                                            </TableCell>

                                            <TableCell sx={{ p: '3px 6px', textAlign: 'center', fontSize: '0.85rem', fontWeight: 700, color: '#27ae60', lineHeight: 1.2 }}>
                                                {worker.DplyWrkr}
                                            </TableCell>

                                            <TableCell sx={{ p: '3px 6px', textAlign: 'center', lineHeight: 1.2 }}>
                                                <Box sx={{ backgroundColor: '#e8f5e9', color: '#2e7d32', fontWeight: 600, fontSize: '0.58rem', px: 0.8, py: 0.1, borderRadius: 1, display: 'inline-block' }}>
                                                    {/* ACTIVE */}
                                                </Box>
                                            </TableCell>

                                            <TableCell sx={{ p: '3px 6px', textAlign: 'center', lineHeight: 1.2 }}>
                                                <Box sx={{ borderBottom: '1.5px dotted #999', width: '60%', mx: 'auto', py: 0.5, fontSize: '0.58rem', color: '#999', fontStyle: 'italic' }}>
                                                    {/* Sign */}
                                                </Box>
                                            </TableCell>

                                            <TableCell
                                                sx={{
                                                    p: '3px 6px',
                                                    fontSize: '0.65rem',
                                                    color: '#6c757d',
                                                    lineHeight: 1.3,
                                                    width: '100px',
                                                    maxWidth: '100px',
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word',
                                                    overflow: 'hidden',
                                                }}
                                            >
                                                {worker.Remark || '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            ))}

            {/* Tiny Footer */}
            <Box
                sx={{
                    mt: 2,
                    py: 1,
                    textAlign: 'center',
                    borderTop: '1px solid #dee2e6',
                }}
            >
                <Typography
                    variant="caption"
                    sx={{ color: '#95a5a6', fontSize: '0.65rem', lineHeight: 1.2 }}
                >
                    Generated: {new Date().toLocaleDateString()} {new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </Typography>
            </Box>
        </Box>
    );
});

PrintPage.displayName = 'PrintPage';
export default PrintPage;